import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { UserRoleRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useDeleteRole = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.deletePermissionsRolesByIdV1(id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ROLE_LIST(projectId) });
      showFlashToast({
        variant: "success",
        message: t("model.role.action.delete.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.role.action.delete.notification.error"),
      });
    },
  });
};

const useCreateRole = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: UserRoleRequest) => api.postPermissionsRolesV1(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ROLE_LIST(projectId) });
      showFlashToast({
        variant: "success",
        message: t("page.role.create-or-edit.create-mode.action.create.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.role.create-or-edit.create-mode.action.create.notification.failure"),
      });
    },
  });
};

const useUpdateRole = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: { id: string; payload: UserRoleRequest }) => api.putPermissionsRolesV1(data.id, data.payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ROLE_LIST(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
      showFlashToast({
        variant: "success",
        message: t("page.role.create-or-edit.edit-mode.action.edit.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.role.create-or-edit.edit-mode.action.edit.notification.failure"),
      });
    },
  });
};

export const permissionMutations = {
  useDeleteRole,
  useCreateRole,
  useUpdateRole,
};
